import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import ChartHelpButton from '../ChartHelpButton';
import {
  getChartColor, getCleanOptions, getSmartYDomain, fobCascadeOptions, pickBestFobCombination,
  hidesPlaceholders, initCap, characteristicKeys, characteristicReader, characteristicFilterOptionKey,
  walkCharacteristicOptions, readRowField,
} from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { getLegendWrapperStyle } from './chartStyleUtils';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';
import { FobAvgYearTooltipBody } from './ChartTooltipContent';
import { useChartTooltipTrigger } from './useChartTooltipTrigger';
import ChartFullscreenTitleControl from '../analytics/ChartFullscreenTitleControl';
import { PresetChartTitle, PresetFilterStrip, presetChartBodyClass } from '../analytics/PresetChartChrome';
import { useIsBelowLg, getResponsiveChartMargin } from '../../hooks/useMediaQuery';
import { singleSelectionLabel, toggleMultiValue, applyMultiOrAllFilter } from '../../../utils/filterLabels';
import { multiSelectDropdownLabel } from './terminalPricePulseTableUtils';

const FOB_AVG_MULTI_KEYS = new Set(['variety', 'sizeGroup', 'size', 'grade', 'storage', 'condition', 'environment', 'quality', 'appearance', 'properties']);

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Color scheme - 3-yr avg is gray, individual years get distinct colors
const CHART_COLORS = {
  threeYrAvg: '#6B7280', // Gray for 3-yr average
  yearColors: [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Orange/Amber
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#06B6D4', // Cyan
  ]
};

// Get line color for a specific year (distinct colors for each year)
const getYearLineColor = (year, selectedYears) => {
  const sortedYears = [...selectedYears].sort((a, b) => b - a); // Most recent first
  const idx = sortedYears.indexOf(year);
  return CHART_COLORS.yearColors[idx % CHART_COLORS.yearColors.length];
};

/**
 * FOB Price Average Chart - Year-over-year price comparison
 * Uses cascading filters like Price Pulse - each filter narrows down available options
 */
const FOBPriceAvgChart = ({
  data = [],
  years = [],
  origins = [],
  filterOptions = null,
  commodity = '',
  forceFiltersOpen = false,
  presetDescription = '',
  analyticsFullscreen = null,
  presetSingleChartFullscreen = false,
  hideHelpButton = false,
}) => {
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  // Collapsed by default. The preset view keeps filters open from lg up via the
  // CSS gate on the strip below; smaller screens stay behind the Filters toggle.
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  const [show3YrAvg, setShow3YrAvg] = useState(true);
  const [granularity, setGranularity] = useState('monthly');

  // Price Type: which price field to use (low, avg, high)
  const [priceType, setPriceType] = useState('avg');

  // Price type labels for display
  const priceTypeLabels = {
    low: 'Avg Low',
    avg: 'Avg',
    high: 'Avg High'
  };

  // Helper to get price based on selected type
  const getPriceByType = (item) => {
    switch (priceType) {
      case 'low': return item.avg_low_price;
      case 'high': return item.avg_high_price;
      default: return item.avg_price;
    }
  };

  // Modal states
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [granularityModalOpen, setGranularityModalOpen] = useState(false);
  const [priceTypeModalOpen, setPriceTypeModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const [sizeModalOpen, setSizeModalOpen] = useState(false);
  const [sizeGroupModalOpen, setSizeGroupModalOpen] = useState(false);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [storageModalOpen, setStorageModalOpen] = useState(false);
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [environmentModalOpen, setEnvironmentModalOpen] = useState(false);
  const [qualityModalOpen, setQualityModalOpen] = useState(false);
  const [appearanceModalOpen, setAppearanceModalOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);

  /** District / shipping point — aligned with FOB Price Pulse & by-origin (`district` on row, optional `origin`). */
  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [originsUserCleared, setOriginsUserCleared] = useState(false);

  // Product characteristic filters (single-select)
  const [selectedType, setSelectedType] = useState('Conventional');
  const [selectedVariety, setSelectedVariety] = useState([]);
  const [selectedSize, setSelectedSize] = useState([]);
  const [selectedSizeGroup, setSelectedSizeGroup] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedStorage, setSelectedStorage] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState([]);
  const [selectedAppearance, setSelectedAppearance] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  // Cucumbers only: Size Group is size_group; Size is size_granularity (crop readSize).
  const gatedCascade = hidesPlaceholders(commodity);
  const applyFobAvgMultiFromRows = (rows) => {
    setSelectedVariety(getCleanOptions(rows, 'variety', commodity));
    setSelectedSizeGroup(getCleanOptions(rows, 'size_group', commodity));
    setSelectedSize(getCleanOptions(rows, characteristicReader('size', commodity), commodity));
    setSelectedGrade(getCleanOptions(rows, 'grade', commodity));
    setSelectedStorage(getCleanOptions(rows, 'storage', commodity));
    setSelectedCondition(getCleanOptions(rows, 'condition', commodity));
    setSelectedEnvironment(getCleanOptions(rows, 'environment', commodity));
    setSelectedQuality(getCleanOptions(rows, 'quality', commodity));
    setSelectedAppearance(getCleanOptions(rows, 'appearance', commodity));
    setSelectedProperties(getCleanOptions(rows, 'properties', commodity));
  };
  const sizeDisplay = gatedCascade ? initCap : undefined;

  // Sort years descending to ensure most recent first
  const sortedYears = useMemo(() => [...years].sort((a, b) => b - a), [years]);

  // Track if initial cascade has run
  const initializedRef = useRef(false);
  const chartTooltipBoundaryRef = useRef(null);
  const { trigger: tooltipTrigger, tooltipDismissKey } = useChartTooltipTrigger(chartTooltipBoundaryRef);
  const isBelowLg = useIsBelowLg();

  const fobAvgTooltipContent = useCallback(
    (props) => <FobAvgYearTooltipBody {...props} theme={theme} fontSize={fontSize} />,
    [theme, fontSize]
  );

  const yearLineDotBase = useMemo(() => ({ r: 4, strokeWidth: 0 }), []);
  const yearLineActiveDotBase = useMemo(
    () => ({
      r: 6,
      stroke: '#fff',
      strokeWidth: 2,
    }),
    []
  );
  const threeYrAvgDotStyle = useMemo(
    () => ({
      r: 3,
      fill: CHART_COLORS.threeYrAvg,
      strokeWidth: 0,
    }),
    []
  );
  const threeYrAvgActiveDotStyle = useMemo(
    () => ({
      r: 5,
      fill: CHART_COLORS.threeYrAvg,
      stroke: '#fff',
      strokeWidth: 2,
    }),
    []
  );

  /**
   * Default characteristic combination for a years/origins/type scope.
   * Chain: Years → Origins → Type → Variety → Size → Grade → Package → Storage → Condition → Environment → Quality → Appearance → Properties
   *
   * The chain still describes how the filter UI cascades, but the values come
   * from scoring whole combinations by how many points they actually plot
   * (see `pickBestFobCombination`) rather than taking the most common value one
   * field at a time — greedy per-field selection optimises each field against
   * the slice the previous one left, and over eleven dependent fields that
   * drifts onto a thin cell with few weeks and no year-over-year history.
   *
   * `granularity`/`priceType` are options rather than reads of live state
   * because the init effect sets both in the same pass it cascades in, so the
   * state it would read is still the pre-init value. They default to the View 2
   * spec (weekly / avg), which is what that effect applies.
   */
  const calculateCascade = useCallback((startingData, options = {}) => {
    const {
      years = null,
      type = null,
      origins: cascadeOrigins = null,
      granularity: scoreGranularity = 'weekly',
      priceType: scorePriceType = 'avg',
    } = options;
    let filtered = startingData;

    // Years - filter first if provided
    if (years && years.length > 0) {
      filtered = filtered.filter(item => years.includes(item.year));
    }

    if (Array.isArray(cascadeOrigins) && cascadeOrigins.length > 0) {
      filtered = filtered.filter((item) => {
        const d = item.district ?? item.origin;
        return d && cascadeOrigins.includes(d);
      });
    }

    // A caller-pinned type (the user picking one) is honoured, but only where
    // it has rows — pinning a type the commodity does not carry empties the
    // slice and blanks every downstream default. With no pin, type is scored
    // like every other field; Conventional only breaks exact ties. It used to
    // be pinned to Conventional here, which hid better-covered types.
    if (type) {
      const withType = filtered.filter(item => item.type === type);
      if (withType.length > 0) filtered = withType;
    }

    return pickBestFobCombination(filtered, {
      granularity: scoreGranularity,
      priceType: scorePriceType,
      commodity,
    });
  }, [commodity]);

  /** Reset only when commodity changes (persist resetKey). Do not reset on every `data` refetch — that runs after layout hydrate and cleared initializedRef, so the init effect overwrote Firestore compare years. */
  useLayoutEffect(() => {
    initializedRef.current = false;
  }, [commodity]);

  const hydrateFOBAvg = useCallback(
    (s) => {
      initializedRef.current = true;
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
      if (typeof s.show3YrAvg === 'boolean') setShow3YrAvg(s.show3YrAvg);
      if (s.granularity === 'monthly' || s.granularity === 'weekly') setGranularity(s.granularity);
      if (s.priceType === 'low' || s.priceType === 'avg' || s.priceType === 'high') setPriceType(s.priceType);

      const sortedNums = sortedYears.map((y) => Number(y));
      let years = Array.isArray(s.selectedYears)
        ? s.selectedYears
            .map((y) => Number(y))
            .filter((y) => Number.isFinite(y) && sortedNums.includes(y))
        : [];
      if (years.length === 0) years = sortedYears.map((y) => Number(y));
      setSelectedYears(years);

      const districtsFromRows = [
        ...new Set(
          data
            .filter((item) => years.includes(item.year))
            .map((item) => item.district ?? item.origin)
            .filter(Boolean)
        ),
      ];
      const orderedDistricts =
        origins && origins.length > 0
          ? origins.filter((o) => districtsFromRows.includes(o))
          : [...districtsFromRows].sort();

      const cleared = s.originsUserCleared === true;
      setOriginsUserCleared(cleared);
      let nextOrigins;
      if (cleared) {
        nextOrigins = Array.isArray(s.selectedOrigins)
          ? s.selectedOrigins.filter((o) => orderedDistricts.includes(o))
          : [];
      } else if (Array.isArray(s.selectedOrigins) && s.selectedOrigins.length > 0) {
        nextOrigins = s.selectedOrigins.filter((o) => orderedDistricts.includes(o));
      } else {
        nextOrigins = orderedDistricts;
      }
      setSelectedOrigins(nextOrigins);

      let filtered = data.filter((item) => years.includes(item.year));
      if (nextOrigins.length > 0) {
        filtered = filtered.filter((item) => {
          const d = item.district ?? item.origin;
          return d && nextOrigins.includes(d);
        });
      }
            // Every load opens on the combination with the most data, ignoring the
      // characteristics saved from the last visit — restoring them brought a
      // user back to whatever thin slice they last drilled into, which reads
      // as a broken chart. Filtering is a within-session action.
      const restored = pickBestFobCombination(filtered, {
        granularity: s.granularity === 'monthly' ? 'monthly' : 'weekly',
        priceType: s.priceType === 'low' || s.priceType === 'high' ? s.priceType : 'avg',
        commodity,
      });

      setSelectedType(restored.type);
      {
        let scoped = filtered;
        if (restored.type) scoped = scoped.filter((item) => item.type === restored.type);
        applyFobAvgMultiFromRows(scoped);
      }
      setSelectedPackage(restored.package);
    },
    [data, sortedYears, origins, commodity]
  );

  const snapshotFOBAvg = useCallback(
    () => ({
      filtersExpanded,
      selectedYears,
      show3YrAvg,
      granularity,
      priceType,
      selectedOrigins,
      originsUserCleared,
      selectedType,
      selectedVariety,
      selectedSize,
      selectedSizeGroup,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    }),
    [
      filtersExpanded,
      selectedYears,
      show3YrAvg,
      granularity,
      priceType,
      selectedOrigins,
      originsUserCleared,
      selectedType,
      selectedVariety,
      selectedSize,
      selectedSizeGroup,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    ]
  );

  usePersistWidgetSnapshot(
    'fob-price-avg',
    commodity,
    !!(data && data.length > 0 && sortedYears.length > 0),
    hydrateFOBAvg,
    snapshotFOBAvg,
    [
      filtersExpanded,
      selectedYears,
      show3YrAvg,
      granularity,
      priceType,
      selectedOrigins,
      originsUserCleared,
      selectedType,
      selectedVariety,
      selectedSize,
      selectedSizeGroup,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    ]
  );

  // Initialize on first data load - set defaults to combination with most data
  useEffect(() => {
    if (!data || data.length === 0 || initializedRef.current) return;

    initializedRef.current = true;

    // Defaults (View 2 spec):
    // - Granularity: weekly
    // - Price: AVG
    // - Compare: current year + last year
    // - 3-yr average: shown
    // - Type: Conventional
    setGranularity('weekly');
    setPriceType('avg');
    setShow3YrAvg(true);

    const currentYear = sortedYears?.[0];
    const lastYear = sortedYears?.[1];
    const defaultYears = [currentYear, lastYear].filter((y) => typeof y === 'number' && Number.isFinite(y));
    setSelectedYears(defaultYears);

    const yearFiltered = data.filter((item) => defaultYears.includes(item.year));
    const districtsFromRows = [
      ...new Set(yearFiltered.map((item) => item.district ?? item.origin).filter(Boolean)),
    ];
    const allODef =
      origins && origins.length > 0
        ? origins.filter((o) => districtsFromRows.includes(o))
        : [...districtsFromRows].sort();
    setSelectedOrigins(allODef);
    setOriginsUserCleared(false);

    // Calculate cascade starting from selected years + origins to find best default filter combination.
    // Type is left unpinned so it competes on data coverage like every other field.
    const cascade = calculateCascade(data, { years: defaultYears, origins: allODef });
    setSelectedType(cascade.type);
    {
      let scoped = yearFiltered;
      if (allODef.length) {
        scoped = scoped.filter((item) => {
          const d = item.district ?? item.origin;
          return d && allODef.includes(d);
        });
      }
      if (cascade.type) scoped = scoped.filter((item) => item.type === cascade.type);
      applyFobAvgMultiFromRows(scoped);
    }
    setSelectedPackage(cascade.package);
  }, [data, sortedYears, origins, calculateCascade]);

  const cascadeFromOrigins = useCallback(
    (nextOrigins) => {
      setSelectedOrigins(nextOrigins);
      setOriginsUserCleared(nextOrigins.length === 0);

      const cascade = calculateCascade(data, {
        years: selectedYears, origins: nextOrigins, granularity, priceType,
      });
      setSelectedType(cascade.type);
      {
        let scoped = data;
        if (selectedYears.length) scoped = scoped.filter((item) => selectedYears.includes(item.year));
        if (nextOrigins.length) {
          scoped = scoped.filter((item) => {
            const d = item.district ?? item.origin;
            return d && nextOrigins.includes(d);
          });
        }
        if (cascade.type) scoped = scoped.filter((item) => item.type === cascade.type);
        applyFobAvgMultiFromRows(scoped);
      }
      setSelectedPackage(cascade.package);
    },
    [data, selectedYears, granularity, priceType, calculateCascade]
  );

  const handleOriginChange = useCallback(
    (origin) => {
      let next;
      if (selectedOrigins.includes(origin)) {
        next = selectedOrigins.filter((o) => o !== origin);
      } else {
        next = [...selectedOrigins, origin];
      }
      cascadeFromOrigins(next);
    },
    [selectedOrigins, cascadeFromOrigins]
  );

  // Handler for TYPE change - re-derives all downstream filters for coverage
  const handleTypeChange = useCallback((newType) => {
    setSelectedType(newType);

    const cascade = calculateCascade(data, {
      years: selectedYears, origins: selectedOrigins, type: newType, granularity, priceType,
    });
    {
      let scoped = data;
      if (selectedYears.length) scoped = scoped.filter((item) => selectedYears.includes(item.year));
      if (selectedOrigins.length) {
        scoped = scoped.filter((item) => {
          const d = item.district ?? item.origin;
          return d && selectedOrigins.includes(d);
        });
      }
      if (newType) scoped = scoped.filter((item) => item.type === newType);
      applyFobAvgMultiFromRows(scoped);
    }
    setSelectedPackage(cascade.package);
  }, [data, selectedYears, selectedOrigins, granularity, priceType, calculateCascade]);

  // Generic cascade handler for filters below type (respects year selection)
  const handleFilterChange = useCallback((level, newValue) => {
    // Build filtered data starting from years, origins, then type
    let filtered = data;
    // Filter by selected years first (top of cascade)
    if (selectedYears.length > 0) {
      filtered = filtered.filter(item => selectedYears.includes(item.year));
    }
    if (selectedOrigins.length > 0) {
      filtered = filtered.filter((item) => {
        const d = item.district ?? item.origin;
        return d && selectedOrigins.includes(d);
      });
    }
    if (selectedType) filtered = filtered.filter(item => item.type === selectedType);

    const filterOrder = characteristicKeys(commodity);
    const filterFields = Object.fromEntries(filterOrder.map((k) => [k, characteristicReader(k, commodity)]));
    const currentValues = {
      variety: selectedVariety,
      sizeGroup: selectedSizeGroup,
      size: selectedSize,
      grade: selectedGrade,
      package: selectedPackage,
      storage: selectedStorage,
      condition: selectedCondition,
      environment: selectedEnvironment,
      quality: selectedQuality,
      appearance: selectedAppearance,
      properties: selectedProperties
    };
    const setters = {
      variety: setSelectedVariety,
      sizeGroup: setSelectedSizeGroup,
      size: setSelectedSize,
      grade: setSelectedGrade,
      package: setSelectedPackage,
      storage: setSelectedStorage,
      condition: setSelectedCondition,
      environment: setSelectedEnvironment,
      quality: setSelectedQuality,
      appearance: setSelectedAppearance,
      properties: setSelectedProperties
    };

    const levelIdx = filterOrder.indexOf(level);

    // Apply all filters up to (but not including) this level
    for (let i = 0; i < levelIdx; i++) {
      const l = filterOrder[i];
      const field = filterFields[l];
      const value = currentValues[l];
      if (Array.isArray(value)) {
        filtered = applyMultiOrAllFilter(filtered, field, value, getCleanOptions(filtered, field, commodity));
      } else if (value) {
        filtered = filtered.filter(item => readRowField(item, field) === value);
      }
    }

    // Apply the new value for this level
    setters[level](newValue);

    if (Array.isArray(newValue)) {
      filtered = applyMultiOrAllFilter(filtered, filterFields[level], newValue, getCleanOptions(filtered, filterFields[level], commodity));
    } else if (newValue) {
      filtered = filtered.filter(item => readRowField(item, filterFields[level]) === newValue);
    }

    // Cascade downstream: score the remaining fields as a whole combination.
    // Keeping a value because it still appears in the new dropdown is not the
    // same as it still having data — that is how the chart ended up nearly
    // empty while every filter still looked reasonable.
    const best = pickBestFobCombination(filtered, { granularity, priceType, commodity });
    for (let i = levelIdx + 1; i < filterOrder.length; i++) {
      const l = filterOrder[i];
      if (FOB_AVG_MULTI_KEYS.has(l)) {
        setters[l](getCleanOptions(filtered, filterFields[l], commodity));
      } else {
        setters[l](best[l] || '');
      }
    }
  }, [data, selectedYears, selectedOrigins, selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality,
      selectedAppearance, selectedProperties, granularity, priceType, commodity]);

  // Handler for YEAR toggle - re-derives ALL product filters for coverage
  const handleYearToggle = useCallback((year) => {
    let newYears;
    if (selectedYears.includes(year)) {
      newYears = selectedYears.filter(y => y !== year);
    } else {
      newYears = [...selectedYears, year].sort((a, b) => b - a);
    }
    setSelectedYears(newYears);

    if (newYears.length > 0) {
      const cascade = calculateCascade(data, {
        years: newYears, origins: selectedOrigins, granularity, priceType,
      });
      setSelectedType(cascade.type);
      {
        let scoped = data.filter((item) => newYears.includes(item.year));
        if (selectedOrigins.length) {
          scoped = scoped.filter((item) => {
            const d = item.district ?? item.origin;
            return d && selectedOrigins.includes(d);
          });
        }
        if (cascade.type) scoped = scoped.filter((item) => item.type === cascade.type);
        applyFobAvgMultiFromRows(scoped);
      }
      setSelectedPackage(cascade.package);
    }
  }, [data, selectedYears, selectedOrigins, granularity, priceType, calculateCascade]);

  // Get available options based on current selections (cascading filters)
  // Chain: Years → Origins → Type → Variety → Size → Grade → etc. (filter_options order)
  const availableOptions = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        origins: [],
        types: [],
        varieties: [],
        sizeGroups: [],
        sizes: [],
        grades: [],
        packages: [],
        storages: [],
        conditions: [],
        environments: [],
        qualities: [],
        appearances: [],
        properties: [],
      };
    }

    let filtered = data;
    if (selectedYears.length > 0) {
      filtered = filtered.filter((item) => selectedYears.includes(item.year));
    }

    const districtsFromData = [
      ...new Set(filtered.map((item) => item.district ?? item.origin).filter(Boolean)),
    ];
    const originOptions =
      origins && origins.length > 0
        ? origins.filter((o) => districtsFromData.includes(o))
        : [...districtsFromData].sort();

    if (selectedOrigins.length > 0) {
      filtered = filtered.filter((item) => {
        const d = item.district ?? item.origin;
        return d && selectedOrigins.includes(d);
      });
    }

    const types = fobCascadeOptions(filterOptions, 'type', getCleanOptions(filtered, 'type', commodity));

    if (selectedType) {
      filtered = filtered.filter((item) => item.type === selectedType);
    }
    const walked = walkCharacteristicOptions(
      filtered,
      characteristicKeys(commodity),
      {
        variety: selectedVariety, sizeGroup: selectedSizeGroup, size: selectedSize, grade: selectedGrade,
        package: selectedPackage, storage: selectedStorage, condition: selectedCondition,
        environment: selectedEnvironment, quality: selectedQuality, appearance: selectedAppearance,
      },
      (key, rows) => fobCascadeOptions(
        filterOptions,
        characteristicFilterOptionKey(key, commodity),
        getCleanOptions(rows, characteristicReader(key, commodity), commodity),
      ),
      (key, item, value) => {
        const got = readRowField(item, characteristicReader(key, commodity));
        if (Array.isArray(value)) return !value.length || value.includes(got);
        return got === value;
      },
    ).options;
    const {
      variety: varieties, sizeGroup: sizeGroups = [], size: sizes, grade: grades, package: packages,
      storage: storages, condition: conditions, environment: environments, quality: qualities,
      appearance: appearances, properties,
    } = walked;

    return {
      origins: originOptions,
      types,
      varieties,
      sizeGroups,
      sizes,
      grades,
      packages,
      storages,
      conditions,
      environments,
      qualities,
      appearances,
      properties,
    };
  }, [data,
    origins,
    filterOptions,
    selectedYears,
    selectedOrigins,
    selectedType,
    selectedVariety,
    selectedSize,
    selectedSizeGroup,
    selectedGrade,
    selectedPackage,
    selectedStorage,
    selectedCondition,
    selectedEnvironment,
    selectedQuality,
    selectedAppearance, commodity]);


  // Filter data by all selected characteristics
  const filteredData = useMemo(() => {
    let rows = data.filter((item) => {
      if (selectedOrigins.length > 0) {
        const d = item.district ?? item.origin;
        if (!d || !selectedOrigins.includes(d)) return false;
      }
      if (selectedType && item.type !== selectedType) return false;
      if (selectedPackage && item.package !== selectedPackage) return false;
      return true;
    });
    rows = applyMultiOrAllFilter(rows, 'variety', selectedVariety, availableOptions.varieties);
    rows = applyMultiOrAllFilter(rows, 'size_group', selectedSizeGroup, availableOptions.sizeGroups);
    rows = applyMultiOrAllFilter(rows, 'item_size', selectedSize, availableOptions.sizes, (r) => readRowField(r, characteristicReader('size', commodity)));
    rows = applyMultiOrAllFilter(rows, 'grade', selectedGrade, availableOptions.grades);
    rows = applyMultiOrAllFilter(rows, 'storage', selectedStorage, availableOptions.storages);
    rows = applyMultiOrAllFilter(rows, 'condition', selectedCondition, availableOptions.conditions);
    rows = applyMultiOrAllFilter(rows, 'environment', selectedEnvironment, availableOptions.environments);
    rows = applyMultiOrAllFilter(rows, 'quality', selectedQuality, availableOptions.qualities);
    rows = applyMultiOrAllFilter(rows, 'appearance', selectedAppearance, availableOptions.appearances);
    rows = applyMultiOrAllFilter(rows, 'properties', selectedProperties, availableOptions.properties);
    return rows;
  }, [data, selectedOrigins, selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality,
      selectedAppearance, selectedProperties, availableOptions, commodity]);

  // Aggregate data and calculate chart data + scorecards
  const { chartData, scorecards } = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return { chartData: [], scorecards: { ytdAvg: null, ytdLabel: '', vsLastYear: null, vs3YrAvg: null, lastYearAvg: null } };
    }

    const aggregated = {};
    filteredData.forEach(item => {
      const year = item.year;
      const period = granularity === 'weekly' ? item.iso_week : item.month;
      const price = getPriceByType(item);

      if (!price || !period) return;

      const key = `${year}-${period}`;
      if (!aggregated[key]) {
        aggregated[key] = { year, period, prices: [] };
      }
      aggregated[key].prices.push(price);
    });

    const byPeriod = {};
    const yearlyPrices = {};

    Object.values(aggregated).forEach(({ year, period, prices }) => {
      const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
      const periodLabel = granularity === 'weekly'
        ? `W${period}`
        : MONTH_LABELS[period - 1];

      if (!byPeriod[period]) {
        byPeriod[period] = { period: periodLabel, periodNum: period };
      }
      byPeriod[period][`${year}`] = Math.round(avgPrice * 100) / 100;

      if (!yearlyPrices[year]) yearlyPrices[year] = [];
      yearlyPrices[year].push(avgPrice);
    });

    // Get years that actually have data after filtering (sorted descending)
    const filteredYears = Object.keys(yearlyPrices).map(Number).sort((a, b) => b - a);
    const filteredCurrentYear = filteredYears[0]; // Most recent year with data
    const filteredLastYear = filteredYears[1]; // Second most recent
    const filteredLast3Years = filteredYears.slice(1, 4); // Years 2, 3, 4 for 3-yr avg

    // Calculate 3-year average for each period
    Object.entries(byPeriod).forEach(([period, periodData]) => {
      const prices = filteredLast3Years
        .map(y => periodData[`${y}`])
        .filter(p => p !== undefined);
      if (prices.length > 0) {
        periodData['3YrAvg'] = Math.round((prices.reduce((a, b) => a + b, 0) / prices.length) * 100) / 100;
      }
    });

    const sortedData = Object.values(byPeriod).sort((a, b) => a.periodNum - b.periodNum);

    // Find the latest period with data for the filtered current year
    const currentYearPeriods = Object.keys(byPeriod)
      .filter(period => byPeriod[period][`${filteredCurrentYear}`] !== undefined)
      .map(Number)
      .sort((a, b) => b - a);

    const latestPeriod = currentYearPeriods[0]; // Most recent period with data

    // For both weekly and monthly: use latest period's price
    let ytdAvg = null;
    let ytdLabel = '';

    if (granularity === 'weekly') {
      // Weekly: show latest week's price
      ytdAvg = latestPeriod ? byPeriod[latestPeriod]?.[`${filteredCurrentYear}`] : null;
      ytdLabel = latestPeriod ? `W${latestPeriod}` : 'Latest';
    } else {
      // Monthly: show latest month's average price
      ytdAvg = latestPeriod ? byPeriod[latestPeriod]?.[`${filteredCurrentYear}`] : null;
      ytdLabel = latestPeriod ? MONTH_LABELS[latestPeriod - 1] : 'Latest';
    }

    // Compare same period for last year (apples-to-apples) - use filtered last year
    const lastYearAvg = latestPeriod && filteredLastYear
      ? byPeriod[latestPeriod]?.[`${filteredLastYear}`]
      : null;

    // 3-Year average for same period - use filtered years
    const periodPrices = filteredLast3Years
      .map(y => byPeriod[latestPeriod]?.[`${y}`])
      .filter(p => p !== undefined);
    const threeYrAvg = periodPrices.length > 0
      ? periodPrices.reduce((a, b) => a + b, 0) / periodPrices.length
      : null;

    const vsLastYear = ytdAvg && lastYearAvg
      ? ((ytdAvg - lastYearAvg) / lastYearAvg) * 100
      : null;

    const vs3YrAvg = ytdAvg && threeYrAvg
      ? ((ytdAvg - threeYrAvg) / threeYrAvg) * 100
      : null;

    return {
      chartData: sortedData,
      scorecards: {
        ytdAvg,
        ytdLabel, // "Mar" for monthly, "W12" for weekly
        currentYear: filteredCurrentYear,
        lastYear: filteredLastYear,
        vsLastYear: vsLastYear !== null ? Math.round(vsLastYear * 10) / 10 : null,
        vs3YrAvg: vs3YrAvg !== null ? Math.round(vs3YrAvg * 10) / 10 : null,
        lastYearAvg: lastYearAvg !== null ? Math.round(lastYearAvg * 100) / 100 : null,
        threeYrAvgValue: threeYrAvg !== null ? Math.round(threeYrAvg * 100) / 100 : null,
      }
    };
  }, [filteredData, granularity, priceType]);

  // Filter chart data by selected years
  const displayChartData = useMemo(() => {
    return chartData.map(item => {
      const filtered = { period: item.period };
      selectedYears.forEach(year => {
        if (item[`${year}`] !== undefined) {
          filtered[`${year}`] = item[`${year}`];
        }
      });
      if (show3YrAvg && item['3YrAvg'] !== undefined) {
        filtered['3YrAvg'] = item['3YrAvg'];
      }
      return filtered;
    });
  }, [chartData, selectedYears, show3YrAvg]);

  // Comparison sub-card component (nested inside main card)
  const ComparisonCard = ({ title, value, delta, subtext = '' }) => {
    const isPositive = delta > 0;
    const isNegative = delta < 0;
    const hasValue = value !== null && value !== undefined;

  return (
      <div className="flex-1 rounded-md p-3 border" style={{ backgroundColor: theme.cardBg, borderColor: theme.borderSecondary }}>
        <div className="text-[10px] mb-1" style={{ color: theme.secondaryText }}>{title}</div>
        <div className={`text-lg font-medium ${
          isPositive ? (isDark ? 'text-red-400' : 'text-red-600') : isNegative ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : ''
        }`} style={!isPositive && !isNegative ? { color: theme.barLabelFill } : {}}>
          {hasValue ? value : '-'}
        </div>
        {subtext && (
          <div className={`text-[11px] mt-1 ${isNegative ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : isPositive ? (isDark ? 'text-red-400' : 'text-red-600') : ''}`} style={!isPositive && !isNegative ? { color: theme.secondaryText } : {}}>
            {subtext}
          </div>
        )}
      </div>
    );
  };

  const helpDescription = "Year-over-year comparison of average FOB (Free On Board) shipping point prices. Compare current year prices against previous years and the 3-year average to identify pricing trends.";

  const presetEmbedded = forceFiltersOpen && !presetSingleChartFullscreen;

  // Single-chart fullscreen: below lg the overlay scrolls, so these boxes grow
  // with their content instead of being squeezed to 0px, and the plot keeps a
  // real height. See the `.preset-fullscreen-*` block in index.css.
  const fsFill = presetSingleChartFullscreen ? ' preset-fullscreen-fill' : '';
  const fsPlot = presetSingleChartFullscreen ? ' preset-fullscreen-plot' : '';

  if (!data || data.length === 0) {
    return (
      <div
        className={
          (forceFiltersOpen
            ? presetSingleChartFullscreen
              ? 'w-full h-full min-h-0 flex-1'
              : 'w-full'
            : 'h-full') + ' flex flex-col bg-transparent'
        }
      >
        <div
          className={
            (presetEmbedded ? '' : 'flex-1 ') + 'flex flex-col min-h-0 ' + (presetChartBodyClass(forceFiltersOpen, presetSingleChartFullscreen))
          }
        >
          {/* Title */}
          <PresetChartTitle
            forceFiltersOpen={forceFiltersOpen}
            title="Average FOB Price Over Year"
            theme={theme}
            fontSize={fontSize}
            titleAfter={!hideHelpButton ? <ChartHelpButton title="Average FOB Price" description={helpDescription} /> : null}
            right={
              <ChartFullscreenTitleControl
                analyticsFullscreen={analyticsFullscreen}
                presetSingleChartFullscreen={presetSingleChartFullscreen}
              />
            }
          />
        <div className={presetEmbedded ? 'py-16 flex items-center justify-center' : 'flex-1 flex items-center justify-center'}>
          <p className="text-center" style={{ fontSize, color: theme.noDataText }}>
            No price data available.<br />Select a different commodity.
          </p>
        </div>
        </div>
      </div>
    );
  }

  const packageFilter = availableOptions.packages.length > 0 && (
    <FilterDropdown
      label="Package"
      value={selectedPackage || 'Select'}
      onClick={() => setPackageModalOpen(true)}
      compact={forceFiltersOpen}
    />
  );

  return (
    <div
      className={
        (forceFiltersOpen
          ? presetSingleChartFullscreen
            ? 'w-full h-full min-h-0 flex-1' + fsFill
            : 'w-full'
          : 'h-full') + ' flex flex-col bg-transparent'
      }
    >
      <div
        className={
          (presetEmbedded ? '' : 'flex-1' + fsFill + ' ') + 'flex flex-col min-h-0 ' + (presetChartBodyClass(forceFiltersOpen, presetSingleChartFullscreen))
        }
      >
        {/* Title */}
        <PresetChartTitle
          forceFiltersOpen={forceFiltersOpen}
          title="Average FOB Price Over Year"
          theme={theme}
          fontSize={fontSize}
          titleAfter={!hideHelpButton ? <ChartHelpButton title="Average FOB Price" description={helpDescription} /> : null}
          right={
            <ChartFullscreenTitleControl
              analyticsFullscreen={analyticsFullscreen}
              presetSingleChartFullscreen={presetSingleChartFullscreen}
            />
          }
        />

        {/* Filters */}
        <div className="flex-shrink-0">
          {/* Collapsible in the widget dashboard, and below lg in the preset view
              too; lg:hidden once forceFiltersOpen since desktop preset keeps the
              strip open via the CSS gate below. */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className={`w-full flex items-center justify-between py-2 border-b flex-shrink-0 ${forceFiltersOpen ? 'lg:hidden' : ''}`}
            style={{ borderColor: theme.borderSecondary }}
          >
            <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>Filters</span>
            {filtersExpanded ? <ChevronUp size={16} style={{ color: theme.secondaryText }} /> : <ChevronDown size={16} style={{ color: theme.secondaryText }} />}
          </button>

        {(forceFiltersOpen || filtersExpanded) && (
          <div className={forceFiltersOpen ? (filtersExpanded ? 'max-lg:pt-3' : 'max-lg:hidden') : undefined}>
          <PresetFilterStrip
            forceFiltersOpen={forceFiltersOpen}
            presetSingleChartFullscreen={presetSingleChartFullscreen}
            analyticsFullscreen={analyticsFullscreen}
            titleHasFullscreenControl
            gridClassName="py-3 space-y-2 flex-shrink-0 border-b border-[#E8EBE9] dark:border-[#3a3c3b]"
          >
            {/* `contents` in preset view so both grids flow into the single strip. */}
            <div className={forceFiltersOpen ? 'contents' : 'grid grid-cols-2 lg:grid-cols-4 gap-3'}>
              <FilterDropdown
                label={t('filters.granularity', 'Granularity')}
                value={granularity === 'weekly' ? t('filters.weekly', 'Weekly') : t('filters.monthly', 'Monthly')}
                onClick={() => setGranularityModalOpen(true)}
              compact={forceFiltersOpen}
              />
              <FilterDropdown
                label={t('filters.priceType', 'Price Type')}
                value={priceTypeLabels[priceType] || priceTypeLabels.avg}
                onClick={() => setPriceTypeModalOpen(true)}
              compact={forceFiltersOpen}
              />
              <FilterDropdown
                label="Compare Years"
                value={singleSelectionLabel(selectedYears, years)
                  ?? (selectedYears.length === years.length ? 'All Years' : `${selectedYears.length} selected`)}
                onClick={() => setYearModalOpen(true)}
              compact={forceFiltersOpen}
              />
              <div className={forceFiltersOpen ? 'min-w-0 lg:shrink-0' : undefined}>
                <label
                  className={
                    forceFiltersOpen
                      ? 'mb-[4px] block text-[11px] font-medium text-[#828382] dark:text-gray-400'
                      : 'block mb-1 font-medium'
                  }
                  style={forceFiltersOpen ? undefined : { fontSize: fontSize - 2, color: theme.secondaryText }}
                >
                  3-Yr Average
                </label>
                <button
                  onClick={() => setShow3YrAvg(!show3YrAvg)}
                  className={
                    forceFiltersOpen
                      ? `flex h-[27px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-[8px] px-[10px] text-[11px] font-medium transition-colors lg:w-auto lg:justify-start ${
                          show3YrAvg
                            ? 'bg-orange-500 text-white'
                            : 'border border-[#E2E2E2] text-[#282A29] hover:border-bloom-green dark:border-[#3a3c3b] dark:text-white'
                        }`
                      : `w-full py-1.5 px-3 rounded-lg font-medium transition-colors ${show3YrAvg ? 'bg-orange-500 text-white' : ''}`
                  }
                  style={
                    forceFiltersOpen
                      ? undefined
                      : {
                          fontSize: fontSize - 2,
                          ...(show3YrAvg
                            ? {}
                            : {
                                backgroundColor: theme.controlBg,
                                borderColor: theme.controlBorder,
                                color: theme.controlText,
                                border: '1px solid',
                              }),
                        }
                  }
                >
                  {show3YrAvg ? 'Shown' : 'Hidden'}
                </button>
              </div>
            </div>
            {/* Product Characteristic Filters — Origins → Type → … (matches Price Pulse) */}
            <div className={forceFiltersOpen ? 'contents' : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2'}>
              {availableOptions.origins.length > 0 && (
                <FilterDropdown
                  label={t('filters.origin')}
                  options={availableOptions.origins}
                  selected={selectedOrigins}
                  allLabel={t('filters.allOrigins')}
                  placeholder={t('filters.selectOrigins')}
                  onClick={() => setOriginModalOpen(true)}
                  fontSize={fontSize}
                compact={forceFiltersOpen}
                />
              )}
              {availableOptions.types.length > 0 && (
                <FilterDropdown
                  label="Type"
                  value={selectedType || 'Select'}
                  onClick={() => setTypeModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {availableOptions.varieties.length > 0 && (
                <FilterDropdown
                  label="Variety"
                  value={multiSelectDropdownLabel(selectedVariety, availableOptions.varieties, {
                    allLabel: 'All Varieties',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setVarietyModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {availableOptions.sizeGroups.length > 0 && (
                <FilterDropdown
                  label="Size Group"
                  value={multiSelectDropdownLabel(selectedSizeGroup, availableOptions.sizeGroups, {
                    allLabel: 'All Size Groups',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setSizeGroupModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {gatedCascade && packageFilter}
              {availableOptions.sizes.length > 0 && (
                <FilterDropdown
                  label="Size"
                  value={multiSelectDropdownLabel(selectedSize, availableOptions.sizes, {
                    allLabel: 'All Sizes',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setSizeModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {availableOptions.grades.length > 0 && (
                <FilterDropdown
                  label="Grade"
                  value={multiSelectDropdownLabel(selectedGrade, availableOptions.grades, {
                    allLabel: 'All Grades',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setGradeModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {!gatedCascade && packageFilter}
              {availableOptions.storages.length > 0 && (
                <FilterDropdown
                  label="Storage"
                  value={multiSelectDropdownLabel(selectedStorage, availableOptions.storages, {
                    allLabel: 'All Storage',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setStorageModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {availableOptions.conditions.length > 0 && (
                <FilterDropdown
                  label="Condition"
                  value={multiSelectDropdownLabel(selectedCondition, availableOptions.conditions, {
                    allLabel: 'All Conditions',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setConditionModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {availableOptions.environments.length > 0 && (
                <FilterDropdown
                  label="Environment"
                  value={multiSelectDropdownLabel(selectedEnvironment, availableOptions.environments, {
                    allLabel: 'All Environments',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setEnvironmentModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {availableOptions.qualities.length > 0 && (
                <FilterDropdown
                  label="Quality"
                  value={multiSelectDropdownLabel(selectedQuality, availableOptions.qualities, {
                    allLabel: 'All Quality',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setQualityModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {availableOptions.appearances.length > 0 && (
                <FilterDropdown
                  label="Appearance"
                  value={multiSelectDropdownLabel(selectedAppearance, availableOptions.appearances, {
                    allLabel: 'All Appearances',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setAppearanceModalOpen(true)}
                compact={forceFiltersOpen}
                />
              )}
              {availableOptions.properties.length > 0 && (
                <FilterDropdown
                  label="Properties"
                  value={multiSelectDropdownLabel(selectedProperties, availableOptions.properties, {
                    allLabel: 'All Properties',
                    nSelectedLabel: (n) => `${n} selected`,
                    emptyLabel: 'Select',
                  })}
                  onClick={() => setPropertiesModalOpen(true)}
                  compact={forceFiltersOpen}
                />
              )}
            </div>
          </PresetFilterStrip>
          </div>
        )}
        </div>

        {/* Chart + Scorecard wrapper - re-dimensions together when container resizes */}
        <div className={(presetEmbedded ? '' : 'flex-1' + fsFill + ' ') + 'min-h-0 flex flex-col'}>
          {/* Unified Scorecard - scales with container */}
          <div className="rounded-lg border w-full min-h-0 shrink box-border" style={{ backgroundColor: theme.cardBg, borderColor: theme.borderSecondary, padding: 'clamp(0.5rem, 2vw, 1rem)', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
            <div className="mb-2 sm:mb-3">
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: theme.secondaryText }}>
                {scorecards.currentYear || '-'} {scorecards.ytdLabel || ''} {priceTypeLabels[priceType]}
              </div>
              {/*
                Inline styles bypass postcss/scalable-font-size.js, so this one
                applies --font-scale by hand to stay responsive to the global
                letter-size control while keeping its fluid clamp.
              */}
              <div className="font-semibold" style={{ color: theme.barLabelFill, fontSize: 'calc(clamp(1.25rem, 4vw, 1.875rem) * var(--font-scale, 1))' }}>
                {scorecards.ytdAvg !== null ? `$${scorecards.ytdAvg}` : '-'}
              </div>
            </div>
            <div className="flex gap-2 sm:gap-3 flex-wrap">
              <ComparisonCard
                title={`vs ${scorecards.lastYear || '-'} (${scorecards.ytdLabel || 'same period'})`}
                value={scorecards.vsLastYear !== null ? `${scorecards.vsLastYear > 0 ? '+' : ''}${scorecards.vsLastYear}%` : null}
                delta={scorecards.vsLastYear}
                subtext={scorecards.vsLastYear !== null && scorecards.lastYearAvg !== null
                  ? `${scorecards.vsLastYear < 0 ? 'Lower' : 'Higher'} ($${scorecards.lastYearAvg})`
                  : ''}
              />
              <ComparisonCard
                title={`vs 3-Yr Avg (${scorecards.ytdLabel || 'same period'})`}
                value={scorecards.vs3YrAvg !== null ? `${scorecards.vs3YrAvg > 0 ? '+' : ''}${scorecards.vs3YrAvg}%` : null}
                delta={scorecards.vs3YrAvg}
                subtext={scorecards.vs3YrAvg !== null && scorecards.threeYrAvgValue !== null
                  ? `${scorecards.vs3YrAvg < 0 ? 'Lower' : 'Higher'} ($${scorecards.threeYrAvgValue})`
                  : ''}
              />
            </div>
          </div>

          {/* Chart — fixed height in preset rail so Recharts has a box without min-h-[820px] on the card */}
          <div
            className={
              (presetEmbedded ? 'preset-chart-plot-avg shrink-0' : 'flex-1 min-h-0' + fsPlot) +
              ' w-full bg-white dark:bg-[#191919] rounded-lg'
            }
            ref={chartTooltipBoundaryRef}
          >
          {displayChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={displayChartData}
                margin={getResponsiveChartMargin({ isBelowLg })}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.gridColor} />
                <XAxis
                  dataKey="period"
                  stroke={theme.axisStroke}
                  tick={{ fontSize: fontSize - 2, fill: theme.tickFill }}
                  angle={granularity === 'weekly' ? -45 : 0}
                  textAnchor={granularity === 'weekly' ? 'end' : 'middle'}
                />
                <YAxis
                  stroke={theme.axisStroke}
                  tick={{ fontSize, fill: theme.tickFill }}
                  domain={getSmartYDomain(displayChartData, [...selectedYears.map(String), ...(show3YrAvg ? ['3YrAvg'] : [])])}
                  allowDataOverflow={false}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  key={tooltipDismissKey}
                  trigger={tooltipTrigger}
                  throttleDelay={50}
                  content={fobAvgTooltipContent}
                />
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={getLegendWrapperStyle({ fontSize: fontSize - 2, paddingTop: 0, bottom: presetEmbedded ? 0 : 6 })}
                  payload={[
                    ...selectedYears.map(year => ({
                      value: String(year),
                      type: 'line',
                      color: getYearLineColor(year, selectedYears)
                    })),
                    ...(show3YrAvg ? [{
                      value: '3-Yr Avg',
                      type: 'line',
                      color: CHART_COLORS.threeYrAvg
                    }] : [])
                  ]}
                />
                {selectedYears.map((year) => {
                  const lineColor = getYearLineColor(year, selectedYears);
                  return (
                    <Line
                      key={year}
                      type="monotone"
                      dataKey={String(year)}
                      stroke={lineColor}
                      strokeWidth={2}
                      dot={{ ...yearLineDotBase, fill: lineColor }}
                      activeDot={{ ...yearLineActiveDotBase, fill: lineColor }}
                      connectNulls
                    />
                  );
                })}
                {show3YrAvg && (
                  <Line
                    type="monotone"
                    dataKey="3YrAvg"
                    stroke={CHART_COLORS.threeYrAvg}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={threeYrAvgDotStyle}
                    activeDot={threeYrAvgActiveDotStyle}
                    connectNulls
                    name="3-Yr Avg"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm" style={{ color: theme.secondaryText }}>No data for selected filters</p>
            </div>
          )}
        </div>
        </div>
      </div>


      {/* Year Selection Modal - Multi-select (top of cascade chain) */}
      <FilterModal
        isOpen={yearModalOpen}
        onClose={() => setYearModalOpen(false)}
        title="Select Years"
        options={sortedYears.map(String)}
        selectedValues={selectedYears.map(String)}
        onSelect={(yearStr) => handleYearToggle(Number(yearStr))}
        multiSelect
        onSelectAll={() => {
          const allYears = [...sortedYears];
          setSelectedYears(allYears);
          const cascade = calculateCascade(data, {
            years: allYears,
            origins: selectedOrigins,
            granularity,
            priceType,
          });
          setSelectedType(cascade.type);
          {
            let scoped = data.filter((item) => allYears.includes(item.year));
            if (selectedOrigins.length) {
              scoped = scoped.filter((item) => {
                const d = item.district ?? item.origin;
                return d && selectedOrigins.includes(d);
              });
            }
            if (cascade.type) scoped = scoped.filter((item) => item.type === cascade.type);
            applyFobAvgMultiFromRows(scoped);
          }
          setSelectedPackage(cascade.package);
        }}
        onClearAll={() => setSelectedYears([])}
      />

      <FilterModal
        isOpen={originModalOpen}
        onClose={() => setOriginModalOpen(false)}
        title={t('filters.selectOrigins')}
        options={availableOptions.origins}
        selectedValues={selectedOrigins}
        onSelect={handleOriginChange}
        multiSelect
        onSelectAll={() => {
          const allO = availableOptions.origins;
          setSelectedOrigins(allO);
          setOriginsUserCleared(false);
          cascadeFromOrigins(allO);
        }}
        onClearAll={() => {
          setSelectedOrigins([]);
          setOriginsUserCleared(true);
          cascadeFromOrigins([]);
        }}
      />

      <FilterModal
        isOpen={granularityModalOpen}
        onClose={() => setGranularityModalOpen(false)}
        title={t('filters.granularity', 'Granularity')}
        options={['monthly', 'weekly']}
        selectedValues={granularity}
        onSelect={(value) => setGranularity(value)}
        displayFormatter={(opt) => (opt === 'weekly' ? t('filters.weekly', 'Weekly') : t('filters.monthly', 'Monthly'))}
      />

      <FilterModal
        isOpen={priceTypeModalOpen}
        onClose={() => setPriceTypeModalOpen(false)}
        title={t('filters.priceType', 'Price Type')}
        options={['low', 'avg', 'high']}
        selectedValues={priceType}
        onSelect={(value) => setPriceType(value)}
        displayFormatter={(opt) => priceTypeLabels[opt] || String(opt)}
      />

      {/* Product Filter Modals - Single-select with cascading */}
      <FilterModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        title="Select Type"
        options={availableOptions.types}
        selectedValues={selectedType}
        onSelect={handleTypeChange}
      />

      <FilterModal
        isOpen={varietyModalOpen}
        onClose={() => setVarietyModalOpen(false)}
        title="Select Variety"
        options={availableOptions.varieties}
        selectedValues={selectedVariety}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedVariety);
          handleFilterChange('variety', next.length ? next : availableOptions.varieties);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('variety', availableOptions.varieties)}
        onClearAll={() => handleFilterChange('variety', availableOptions.varieties)}
      />

      <FilterModal
        isOpen={sizeGroupModalOpen}
        onClose={() => setSizeGroupModalOpen(false)}
        title="Select Size Group"
        options={availableOptions.sizeGroups}
        selectedValues={selectedSizeGroup}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedSizeGroup);
          handleFilterChange('sizeGroup', next.length ? next : availableOptions.sizeGroups);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('sizeGroup', availableOptions.sizeGroups)}
        onClearAll={() => handleFilterChange('sizeGroup', availableOptions.sizeGroups)}
        displayFormatter={sizeDisplay}
      />

      <FilterModal
        isOpen={sizeModalOpen}
        onClose={() => setSizeModalOpen(false)}
        title="Select Size"
        options={availableOptions.sizes}
        selectedValues={selectedSize}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedSize);
          handleFilterChange('size', next.length ? next : availableOptions.sizes);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('size', availableOptions.sizes)}
        onClearAll={() => handleFilterChange('size', availableOptions.sizes)}
        displayFormatter={sizeDisplay}
      />

      <FilterModal
        isOpen={gradeModalOpen}
        onClose={() => setGradeModalOpen(false)}
        title="Select Grade"
        options={availableOptions.grades}
        selectedValues={selectedGrade}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedGrade);
          handleFilterChange('grade', next.length ? next : availableOptions.grades);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('grade', availableOptions.grades)}
        onClearAll={() => handleFilterChange('grade', availableOptions.grades)}
      />

      <FilterModal
        isOpen={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        title="Select Package"
        options={availableOptions.packages}
        selectedValues={selectedPackage}
        onSelect={(value) => handleFilterChange('package', value)}
      />

      <FilterModal
        isOpen={storageModalOpen}
        onClose={() => setStorageModalOpen(false)}
        title="Select Storage"
        options={availableOptions.storages}
        selectedValues={selectedStorage}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedStorage);
          handleFilterChange('storage', next.length ? next : availableOptions.storages);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('storage', availableOptions.storages)}
        onClearAll={() => handleFilterChange('storage', availableOptions.storages)}
      />

      <FilterModal
        isOpen={conditionModalOpen}
        onClose={() => setConditionModalOpen(false)}
        title="Select Condition"
        options={availableOptions.conditions}
        selectedValues={selectedCondition}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedCondition);
          handleFilterChange('condition', next.length ? next : availableOptions.conditions);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('condition', availableOptions.conditions)}
        onClearAll={() => handleFilterChange('condition', availableOptions.conditions)}
      />

      <FilterModal
        isOpen={environmentModalOpen}
        onClose={() => setEnvironmentModalOpen(false)}
        title="Select Environment"
        options={availableOptions.environments}
        selectedValues={selectedEnvironment}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedEnvironment);
          handleFilterChange('environment', next.length ? next : availableOptions.environments);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('environment', availableOptions.environments)}
        onClearAll={() => handleFilterChange('environment', availableOptions.environments)}
      />

      <FilterModal
        isOpen={qualityModalOpen}
        onClose={() => setQualityModalOpen(false)}
        title="Select Quality"
        options={availableOptions.qualities}
        selectedValues={selectedQuality}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedQuality);
          handleFilterChange('quality', next.length ? next : availableOptions.qualities);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('quality', availableOptions.qualities)}
        onClearAll={() => handleFilterChange('quality', availableOptions.qualities)}
      />

      <FilterModal
        isOpen={appearanceModalOpen}
        onClose={() => setAppearanceModalOpen(false)}
        title="Select Appearance"
        options={availableOptions.appearances}
        selectedValues={selectedAppearance}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedAppearance);
          handleFilterChange('appearance', next.length ? next : availableOptions.appearances);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('appearance', availableOptions.appearances)}
        onClearAll={() => handleFilterChange('appearance', availableOptions.appearances)}
      />

      <FilterModal
        isOpen={propertiesModalOpen}
        onClose={() => setPropertiesModalOpen(false)}
        title="Select Properties"
        options={availableOptions.properties}
        selectedValues={selectedProperties}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedProperties);
          handleFilterChange('properties', next.length ? next : availableOptions.properties);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('properties', availableOptions.properties)}
        onClearAll={() => handleFilterChange('properties', availableOptions.properties)}
      />
    </div>
  );
};

export default FOBPriceAvgChart;
